/**
 * recommendationsApi — typed client for the Tappya Recommendation Engine
 * BFF routes (FR-019 Phase 1, online surface).
 *
 * Not part of the generated online-ordering-service SDK yet, so the types
 * below are hand-written to match the backend contract. Mirrors the
 * `apiFetch` usage pattern in `services.ts` / `reviewApi.ts`.
 *
 * Contract notes:
 * - An empty `items` array is a NORMAL response (kill switch / degraded
 *   upstream). Recommendations must never break a screen — callers catch
 *   failures and render nothing.
 * - Clicks are fire-and-forget (202): `recordClick` swallows its own errors.
 */
import { apiFetch } from "./http";

export type RecommendationTrigger =
  | "menu_browse"
  | "cart_view"
  | "checkout"
  | "post_order";

export interface RecommendationCartLine {
  itemId: string;
  quantity: number;
}

export interface RecommendationItem {
  recommendationId: string;
  itemId: string;
  name: string;
  price: number;
  imageUrl: string | null;
  categoryId: string | null;
  rank: number;
  sourceCode: string;
  reasonCode: string;
  /** Human-readable "why" line, already resolved server-side. */
  reason: string;
  score: number;
}

export interface RecommendationsRequest {
  branchId: string;
  trigger: RecommendationTrigger;
  cart: RecommendationCartLine[];
  customerId?: string;
  allergens?: string[];
  /** Default 5, max 10 (enforced server-side). */
  limit?: number;
}

export interface RecommendationsResponse {
  branchId: string;
  surface: "online";
  trigger: string;
  items: RecommendationItem[];
}

export interface RecommendationClickRequest {
  recommendationId: string;
  itemId: string;
  branchId: string;
  customerId?: string;
}

export const recommendationsApi = {
  /** POST /v1/online-ordering/recommendations */
  fetch(payload: RecommendationsRequest): Promise<RecommendationsResponse> {
    return apiFetch<RecommendationsResponse>("/v1/online-ordering/recommendations", {
      method: "POST",
      body: payload,
    });
  },

  /**
   * POST /v1/online-ordering/recommendations/clicks — fire-and-forget (202).
   * Never throws: a dropped click must not affect the add-to-cart flow.
   */
  recordClick(payload: RecommendationClickRequest): void {
    apiFetch("/v1/online-ordering/recommendations/clicks", {
      method: "POST",
      body: payload,
    }).catch((err) => {
      console.warn("[recs] click attribution dropped (non-fatal)", err);
    });
  },
};
